import React, { useEffect, useState } from 'react';
import axiosClient from '../../api/axiosClient';
import { ShieldAlert, Crosshair, Fingerprint, Activity, Server, Target, Zap, Download, Globe } from 'lucide-react';

const IntruderMapPage = () => {
    const [intrusions, setIntrusions] = useState([]);
    const [selectedHacker, setSelectedHacker] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        const fetchIntel = async () => {
            try {
                const data = await axiosClient.get('/admin/sentinel/hacker-intel');
            
                // Transform grouped object back to flat list for the sidebar
                let rawList = [];
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    Object.values(data).forEach(group => {
                        if (group.attacks) rawList = [...rawList, ...group.attacks];
                    });
                } else {
                    rawList = Array.isArray(data) ? data : [];
                }

                const enrichedData = rawList.map(intel => ({
                    ...intel,
                    proxy_chain: intel.proxy_chain || ["Route Obfuscated"],
                    ja3_fingerprint: intel.ja3_fingerprint || "N/A",
                    os_guess: intel.os_guess || "Unknown",
                    confidence: intel.confidence || 98,
                    trace_method: intel.trace_method || "Neural Ambush"
                }));
                setIntrusions(enrichedData);
                if (enrichedData.length > 0 && !selectedHacker) {
                    setSelectedHacker(enrichedData[0]);
                }
            } catch (err) { console.error("Intel fetch failed", err); }
        };
        fetchIntel();
        const interval = setInterval(fetchIntel, 30000);
        return () => clearInterval(interval);
    }, [selectedHacker]);

    const downloadPDF = async (id, ip) => {
        setIsDownloading(true);
        // Changed from toast.loading to console log
        console.log(`[CHRONOS-EXPORT] Forging Forensic PDF for ${ip}...`); 
        
        try {
            const response = await axiosClient.get(`/admin/sentinel/hacker-intel/${id}/pdf`, {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `AIRS_Forensic_Report_${ip}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            // Changed from toast.success to console log
            console.log("[CHRONOS-EXPORT] Evidence Secured."); 
        } catch (err) {
            console.error("PDF Download Failed:", err);
            // Changed from toast.error to standard alert
            alert("Forensic Export Failed: Native AIRS Analysis Engine handshake timed out.");
        } finally { 
            setIsDownloading(false); 
        }
    };

    return (
        <div className="flex flex-col h-[calc(100vh-100px)] bg-[#0b0f19] text-white font-mono overflow-hidden rounded-2xl border border-slate-800">
            {/* --- HEADER --- */}
            <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-slate-900/50">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                        <Crosshair className="text-red-500 animate-pulse" size={24} />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold tracking-tighter">PROJECT CHRONOS: DE-ANONYMIZATION</h1>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest">Sovereign Intelligence Division</p>
                    </div>
                </div>
                <div className="hidden md:flex items-center gap-3 bg-slate-800/50 px-3 py-1.5 rounded-full border border-slate-700">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-ping"></div>
                    <span className="text-[10px] text-green-400 font-bold uppercase">Grid Online</span>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* --- LEFT: INTRUDER LIST --- */}
                <div className="w-80 border-r border-slate-800 bg-slate-900/20 flex flex-col">
                    <div className="p-3 border-b border-slate-800 bg-slate-800/40 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                        Identified Adversaries ({intrusions.length})
                    </div>
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
                        {intrusions.map((intel) => (
                            <div 
                                key={intel.id} 
                                onClick={() => setSelectedHacker(intel)}
                                className={`p-3 mb-2 border rounded-xl cursor-pointer transition-all duration-300 ${
                                    selectedHacker?.id === intel.id 
                                    ? 'bg-red-500/10 border-red-500/50 shadow-lg' 
                                    : 'bg-slate-800/30 border-slate-800 hover:border-slate-600'
                                }`}
                            >
                                <div className="flex justify-between items-center mb-1">
                                    <span className={`text-sm font-bold ${selectedHacker?.id === intel.id ? 'text-red-400' : 'text-slate-300'}`}>
                                        {intel.real_ip}
                                    </span>
                                    <span className="text-[9px] text-slate-500">{new Date(intel.captured_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                </div>
                                <div className="text-[10px] text-slate-500 flex items-center gap-1.5 truncate">
                                    <Target size={10} className="text-yellow-600" /> {intel.hostname || "Server Hit"}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* --- RIGHT: THE WAR ROOM --- */}
                <div className="flex-1 overflow-y-auto custom-scrollbar bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-slate-900 via-[#0b0f19] to-[#0b0f19] p-6">
                    {selectedHacker ? (
                        <div className="max-w-5xl mx-auto space-y-6">
                            
                            {/* TOP: RADAR & IDENTITY */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-2 bg-slate-900/60 border border-slate-800 rounded-3xl p-8 relative overflow-hidden flex items-center justify-center min-h-[350px]">
                                    <Globe size={300} className="text-slate-800 opacity-20 absolute" />
                                    <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10"></div>
                                    
                                    <div className="z-10 text-center">
                                        <h2 className="text-[10px] text-slate-500 tracking-[0.3em] uppercase mb-2">Target De-Anonymized</h2>
                                        <p className="text-5xl font-black text-red-500 tracking-tighter mb-6 drop-shadow-[0_0_15px_rgba(239,68,68,0.4)]">
                                            {selectedHacker.real_ip}
                                        </p>
                                        <div className="flex flex-col items-center gap-4">
                                            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full text-green-400 text-xs font-bold">
                                                <Zap size={14} /> AI CONFIDENCE: {selectedHacker.confidence}%
                                            </div>
                                            <button 
                                                disabled={isDownloading}
                                                onClick={() => downloadPDF(selectedHacker.id, selectedHacker.real_ip)}
                                                className="flex items-center gap-2 px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-700 text-white rounded-xl font-bold transition-all text-xs uppercase tracking-widest shadow-xl shadow-red-900/20"
                                            >
                                                <Download size={16} /> {isDownloading ? "Forging..." : "Download Official Evidence"}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* SOVEREIGN STATUS */}
                                <div className="bg-slate-900/80 border border-red-500/30 rounded-3xl p-6 flex flex-col justify-between shadow-2xl">
                                    <div>
                                        <h3 className="text-xs font-bold text-red-400 tracking-widest mb-6 flex items-center gap-2 uppercase">
                                            <ShieldAlert size={16} /> Sovereign Audit
                                        </h3>
                                        <div className="space-y-4">
                                            <StatusRow label="Integrity Guard" val="LOCKED" color="text-green-500" />
                                            <StatusRow label="Backbolt Trace" val="ANCHORED" color="text-blue-400" />
                                            <StatusRow label="Neural Mirage" val="ACTIVE" color="text-purple-400" />
                                        </div>
                                    </div>
                                    <div className="mt-6 p-4 bg-black/40 rounded-2xl border border-white/5">
                                        <p className="text-[10px] text-slate-400 leading-relaxed italic text-center">
                                            "Identity established via Project Chronos. Subject trapped in neural labyrinth for forensic harvest."
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* BOTTOM: DATA GRIDS */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-10">
                                {/* FINGERPRINT */}
                                <div className="bg-slate-900/40 border border-slate-800 rounded-3xl p-6 hover:border-slate-700 transition-colors">
                                    <h3 className="text-[10px] font-bold text-slate-500 tracking-widest mb-6 flex items-center gap-2 uppercase">
                                        <Fingerprint size={16} className="text-blue-500" /> Captured Machine DNA
                                    </h3>
                                    <div className="space-y-5">
                                        {/* If data exists in forensic_dossier or hardware_dna from backend */}
                                        <DataPoint 
                                            label="GPU RENDERER (UNMASKED)" 
                                            val={selectedHacker.forensic_dossier?.gpu || selectedHacker.hardware_dna?.gpu || "Masking Detected"} 
                                        />
                                        <div className="grid grid-cols-2 gap-4">
                                            <DataPoint label="SCREEN RESOLUTION" val={selectedHacker.forensic_dossier?.res || "N/A"} />
                                            <DataPoint label="TIMEZONE" val={selectedHacker.forensic_dossier?.tz || "UTC"} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <DataPoint label="CPU CORES" val={selectedHacker.forensic_dossier?.cores || "N/A"} />
                                            <DataPoint label="RAM SIZE" val={selectedHacker.forensic_dossier?.mem ? `${selectedHacker.forensic_dossier.mem} GB` : "N/A"} />
                                        </div>
                                        <div className="p-3 bg-blue-500/5 rounded-xl border border-blue-500/20">
                                            <span className="block text-[9px] text-blue-400 uppercase font-bold mb-1">INTERNAL LAN IP (WebRTC LEAK)</span>
                                            <span className="text-xs text-blue-300 font-mono font-bold">
                                                {selectedHacker.forensic_dossier?.local_ip || "Tracing..."}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* ATTACK & PROXY */}
                                <div className="bg-slate-900/40 border border-slate-800 rounded-3xl p-6 hover:border-slate-700 transition-colors">
                                    <h3 className="text-[10px] font-bold text-slate-500 tracking-widest mb-6 flex items-center gap-2 uppercase">
                                        <Activity size={16} className="text-orange-500" /> Attack Vector
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="p-3 bg-red-500/5 rounded-xl border border-red-500/10">
                                            <span className="block text-[9px] text-slate-500 uppercase mb-1">Primary Vector</span>
                                            <span className="text-xs text-red-400 font-bold">{selectedHacker.attack_vector}</span>
                                        </div>
                                        <div>
                                            <span className="block text-[9px] text-slate-500 uppercase mb-3">Proxy Chain Bypassed</span>
                                            <div className="space-y-2">
                                                {selectedHacker.proxy_chain.map((proxy, idx) => (
                                                    <div key={idx} className="flex items-center gap-3 text-[10px] text-slate-400 bg-slate-800/40 p-2.5 rounded-lg border border-white/5">
                                                        <Server size={12} className="text-slate-600" /> {proxy}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-slate-600">
                            <Globe size={80} className="mb-4 opacity-20 animate-pulse" />
                            <p className="text-sm tracking-widest uppercase">Initializing Neural Radar...</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const StatusRow = ({ label, val, color }) => (
    <div className="flex justify-between items-center py-1">
        <span className="text-[10px] text-slate-500 uppercase">{label}</span>
        <span className={`text-[10px] font-black tracking-widest ${color}`}>{val}</span>
    </div>
);

const DataPoint = ({ label, val }) => (
    <div>
        <span className="block text-[9px] text-slate-600 uppercase font-bold mb-1">{label}</span>
        <span className="text-xs text-slate-300 font-medium">{val}</span>
    </div>
);

export default IntruderMapPage;